import { useEffect, useState, type ReactNode } from 'react';
import { Alert, Spinner, Text } from '@chakra-ui/react';
import type { Bridge } from '@casa/bridge';
import { writeDashboard, type Connection, type Screen, type ScreenTile } from '@casa/model';
import { parseOrThrow, readSource } from '../data/dashboard.ts';
import { changeInStore, listStore, readFromStore, type WritableStore } from '../data/stores.ts';
import { ChromeDialog } from '../ui/chrome_dialog.tsx';
import { SettingsGroup, SettingsRow } from '../settings/settings_ui.tsx';
import { copyTileInto } from './dashboard_edits.ts';

// Copying the selected tile to another dashboard in the same store (dashboard-editor L-14, §4.3):
// the store's other dashboards, then the target's screens when it has more than one. The target is
// changed in its store at once; the dashboard being edited is not.

type Step =
    | { kind: 'loading' }
    | { kind: 'unavailable'; why: string }
    | { kind: 'dashboards'; store: WritableStore; names: string[] }
    | { kind: 'screens'; store: WritableStore; name: string; screens: Screen[] };

const STORE_NAMES: Record<WritableStore, string> = { device: 'this device', casa: 'the Tilez Hub', cloud: 'Tilez Cloud' };

function messageOf(error: unknown) : string {
    return error instanceof Error ? error.message : String(error);
}

export interface CopyTileDialogProps {
    shell: Bridge;
    tile: ScreenTile;
    /** The edited dashboard's connections, which the tile's sources name. */
    connections: Readonly<Record<string, Connection>>;
    /** Copied: where to, for a notice. */
    onCopied: (where: string) => void;
    onClose: () => void;
}

export function CopyTileDialog({ shell, tile, connections, onCopied, onClose }: CopyTileDialogProps) : ReactNode {
    const [step, setStep] = useState<Step>({ kind: 'loading' });
    const [problem, setProblem] = useState<string>();
    const [isBusy, setBusy] = useState(false);

    useEffect(() => {
        let isCurrent = true;
        const load = async () : Promise<void> => {
            const source = await readSource(shell);
            if (source?.kind !== 'device' && source?.kind !== 'casa' && source?.kind !== 'cloud') {
                setStep({ kind: 'unavailable', why: 'This dashboard comes from a web address, which can only be read: there are no other dashboards beside it to copy to.' });

                return;
            }

            const names = (await listStore(shell, source.kind)).map((entry) => entry.name).filter((name) => name !== source.name);
            if (isCurrent) {
                setStep({ kind: 'dashboards', store: source.kind, names });
            }
        };
        load().catch((error: unknown) => isCurrent && setStep({ kind: 'unavailable', why: messageOf(error) }));

        return () => {
            isCurrent = false;
        };
    }, [shell]);

    const copy = async (store: WritableStore, name: string, screenIndex: number, screenName: string) : Promise<void> => {
        setBusy(true);
        setProblem(undefined);
        try {
            await changeInStore(shell, store, name, (text) => {
                const copied = copyTileInto(parseOrThrow(text, `"${name}"`), screenIndex, tile, connections);

                return writeDashboard(text, copied.dashboard, copied.origins);
            });
            onCopied(`"${name}", screen "${screenName}"`);
        } catch (error) {
            setProblem(`Not copied: ${messageOf(error)}`);
        } finally {
            setBusy(false);
        }
    };

    const pickDashboard = async (store: WritableStore, name: string) : Promise<void> => {
        setBusy(true);
        setProblem(undefined);
        try {
            const { screens } = parseOrThrow(await readFromStore(shell, store, name), `"${name}"`);
            if (screens.length === 1) {
                await copy(store, name, 0, screens[0]!.name);
            } else {
                setStep({ kind: 'screens', store, name, screens });
            }
        } catch (error) {
            setProblem(`Not copied: ${messageOf(error)}`);
        } finally {
            setBusy(false);
        }
    };

    let body: ReactNode;
    switch (step.kind) {
        case 'loading':
            body = <Spinner />;
            break;
        case 'unavailable':
            body = <Text color="fg.muted">{step.why}</Text>;
            break;
        case 'dashboards':
            body = step.names.length === 0 ? (
                <Text color="fg.muted">There is no other dashboard on {STORE_NAMES[step.store]}.</Text>
            ) : (
                <SettingsGroup title={`On ${STORE_NAMES[step.store]}`}>
                    {step.names.map((name) => (
                        <SettingsRow key={name} icon="table-cells-large" title={name} onClick={isBusy ? undefined : () => void pickDashboard(step.store, name)} />
                    ))}
                </SettingsGroup>
            );
            break;
        case 'screens':
            body = (
                <SettingsGroup title={`Screens of "${step.name}"`}>
                    {step.screens.map((screen, index) => (
                        <SettingsRow
                            key={index}
                            icon={screen.icon ?? 'square'}
                            title={screen.name}
                            opens={false}
                            onClick={isBusy ? undefined : () => void copy(step.store, step.name, index, screen.name)}
                        />
                    ))}
                </SettingsGroup>
            );
            break;
    }

    return (
        <ChromeDialog title="Copy to another dashboard" onClose={onClose} size="sm" focusesField={false}>
            {body}
            {problem ? (
                <Alert.Root status="error" mt="3">
                    <Alert.Indicator />
                    <Alert.Title>{problem}</Alert.Title>
                </Alert.Root>
            ) : null}
        </ChromeDialog>
    );
}
